<script setup lang="ts">
import CatalogFiltersContainer from '~/layers/catalog/components/filter/CatalogFiltersContainer.vue'
import { useCatalogStore } from '~/layers/catalog/store/catalog'
import { useCatalogGridStore } from '~/layers/catalog/store/catalogGrid'

const gridStore = useCatalogGridStore()
const store = useCatalogStore()
const bus = useCatalogEventBus()
const fixedFooterEl = ref(null)
const { top } = useFooterFixedIphone(fixedFooterEl)

const isOpened = ref(false)
const scrollPosition = shallowRef(0)
const initialFilters = shallowRef('')

const getFiltersSnapshot = () =>
  JSON.stringify({
    categories: [...store.selectedCategories].sort(),
    filters: store.filters
      .map(({ code, payload }) => ({
        code,
        ...('options' in payload
          ? { options: [...payload.options].sort() }
          : { from: payload.range.from, to: payload.range.to }),
      }))
      .sort((a, b) => a.code.localeCompare(b.code)),
  })

const selectedFiltersCount = computed(() => {
  const filtersCount = store.filters.reduce((count, filter) => {
    if ('options' in filter.payload)
      return count + filter.payload.options.length
    if ('range' in filter.payload) return count + 1

    return count
  }, 0)

  return store.selectedCategories.length + filtersCount
})

const closeAt = (scrollTop: number) => {
  isOpened.value = false

  const scrollBehavior = document.documentElement.style.scrollBehavior
  document.documentElement.style.scrollBehavior = 'auto'
  document.body.classList.remove('no-scroll')
  document.body.style.top = ''
  window.scrollTo(0, scrollTop)

  requestAnimationFrame(() => {
    document.documentElement.style.scrollBehavior = scrollBehavior
  })
}

const close = () => closeAt(scrollPosition.value)

const closeAndScrollToTop = () => closeAt(0)

const open = () => {
  initialFilters.value = getFiltersSnapshot()
  scrollPosition.value = window.scrollY
  isOpened.value = true
  document.body.style.top = `-${scrollPosition.value}px`
  document.body.classList.add('no-scroll')
}

const catalogFilters = useCatalogFilter()

const reset = () => {
  catalogFilters.reset()
  catalogFilters.resetErrors()
  close()
}

const apply = () => {
  bus.emit('mobileFilters:apply')
  if (catalogFilters.errors().length > 0) return
  if (getFiltersSnapshot() === initialFilters.value) {
    close()
    return
  }

  catalogFilters.apply()
  closeAndScrollToTop()
}
</script>

<template>
  <div class="catalog__top-text catalog_mobile">
    <div @click="open">
      Фильтры{{ selectedFiltersCount ? ` (${selectedFiltersCount})` : '' }}
    </div>
    <Teleport to="body">
      <div
        v-if="isOpened"
        class="filters filters_active"
      >
        <div class="filters__menu">
          <div class="filters__top">
            <h3 class="filters__title-mobile">Фильтры</h3>
            <h3 class="filters__title-tablet">Фильтр</h3>
            <div
              class="filters__close"
              @click="apply"
            >
              <svg-icon
                height="20"
                name="icon-close"
                width="20"
              />
            </div>
          </div>

          <CatalogFiltersContainer :filters="gridStore.filters">
            <template #posieBanner>
              <PosieMiniBanner />
            </template>
          </CatalogFiltersContainer>

          <div
            ref="fixedFooterEl"
            :style="top > 0 ? { top: `${top}px` } : {}"
            class="filters__buttons"
            :class="{ 'filters__buttons-iphone': top > 0 }"
          >
            <button
              class="filters__button filters__button_secondary"
              @click="reset"
            >
              Сбросить всё
            </button>
            <button
              class="filters__button filters__button_primary"
              @click="apply"
            >
              Применить
            </button>
          </div>
        </div>

        <div
          class="filters__overlay filters__overlay_active"
          @click="apply"
        ></div>
      </div>
    </Teleport>
  </div>
</template>

<style lang="scss">
@use '/assets/scss/components/filters.scss' as *;
</style>
